import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useUserStore, useOrganizationStore, usePostsStore } from '../stores/StoreProvider';
import { FeedList } from '../components/feed';
import { useFeed } from '../hooks/useFeed';
import { 
  Users, 
  Activity, 
  Building,
  User
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const Organization = observer(() => {
  const userStore = useUserStore();
  const organizationStore = useOrganizationStore();
  const postsStore = usePostsStore();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeTab, setActiveTab] = useState('feed'); // 'feed' or 'members'
  
  // Get organization from user's organization
  const targetOrganization = userStore.profile?.organization;

  useEffect(() => {
    if (targetOrganization && targetOrganization.id) {
      organizationStore.fetchOrgFeed({ page: 1, append: false, organizationId: targetOrganization.id });
      organizationStore.fetchMembers({ page: 1, append: false, organizationId: targetOrganization.id });
      postsStore.fetchPosts({ organizationId: targetOrganization.id });
    }
  }, [targetOrganization?.id, userStore.isAuthenticated]);

  const onRefresh = async () => {
    await Promise.all([
      organizationStore.fetchOrgFeed({ page: 1, append: false, organizationId: targetOrganization?.id }),
      organizationStore.fetchMembers({ page: 1, append: false, organizationId: targetOrganization?.id }),
      postsStore.fetchPosts({ organizationId: targetOrganization?.id }),
    ]);
  };

  const handleLoadMore = useCallback(() => {
    if (!organizationStore.loading && organizationStore.hasMoreFeed) {
      organizationStore.fetchOrgFeed({ page: organizationStore.feedPage, append: true });
    }
  }, [organizationStore]);

  const handleLoadMoreMembers = useCallback(() => {
    if (!organizationStore.membersLoading && organizationStore.hasMoreMembers) {
      organizationStore.fetchMembers({ page: organizationStore.membersPage, append: true, organizationId: targetOrganization?.id });
    }
  }, [organizationStore, targetOrganization]);

  const handleMemberPress = (member) => {
    // TODO: Navigate to member's profile
    toast.info('Profile navigation coming soon!');
  };

  const handleVideoPress = (highlight) => {
    setSelectedVideo(highlight);
    setShowVideoModal(true);
  };

  const handleEditGame = (game) => {
    // TODO: Implement edit game functionality
    toast.info('Edit game functionality coming soon!');
  };

  const handleDeleteGame = async (gameId) => {
    try {
      await userStore.rootStore.gamesStore.deleteGame(gameId);
      await organizationStore.fetchOrgFeed({ page: 1, append: false, organizationId: targetOrganization?.id });
      toast.success('Game deleted successfully');
    } catch (error) {
      toast.error('Failed to delete game');
    }
  };

  const handleEditHighlight = (highlight) => {
    // TODO: Implement edit highlight functionality
    toast.info('Edit highlight functionality coming soon!');
  };

  const handleDeleteHighlight = async (highlightId) => {
    try {
      await userStore.rootStore.highlightsStore.deleteHighlight(highlightId);
      await organizationStore.fetchOrgFeed({ page: 1, append: false, organizationId: targetOrganization?.id });
      toast.success('Highlight deleted successfully');
    } catch (error) {
      toast.error('Failed to delete highlight');
    }
  };

  const handleEditPost = (post) => {
    // TODO: Implement edit post functionality
    toast.info('Edit post functionality coming soon!');
  };

  const handleDeletePost = async (postId) => {
    try {
      await postsStore.deletePost(postId);
      await postsStore.fetchPosts({ organizationId: targetOrganization?.id });
      toast.success('Post deleted successfully');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setSelectedVideo(null);
  };

  if (!targetOrganization || !targetOrganization.id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Building className="mx-auto text-gray-400" size={48} />
          <p className="mt-2 text-gray-500">Loading organization feed...</p>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (organizationStore.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-lg text-red-600 mb-4">Error loading feed</p>
          <p className="text-sm text-gray-500 mb-6">
            {organizationStore.error.includes('Network request failed') 
              ? 'Unable to connect to server. Please check your connection and try again.'
              : organizationStore.error
            }
          </p>
          <button 
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            onClick={() => organizationStore.fetchOrgFeed({ page: 1, append: false })}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Separate different content types from organization feed
  const orgFeed = organizationStore.feed || [];
  const orgGames = orgFeed.filter(item => item.content_type === 'game' || (!item.content_type && !item.media_url && !item.content));
  const orgHighlights = orgFeed.filter(item => item.content_type === 'highlight' || (item.media_url && item.media_type));
  const orgPosts = orgFeed.filter(item => item.content_type === 'post' || (item.content && !item.media_url));

  // Debug logging
  console.log('🔍 Organization: Raw feed data:', orgFeed);
  console.log('🔍 Organization: Separated highlights:', orgHighlights);
  console.log('🔍 Organization: Separated games:', orgGames);
  console.log('🔍 Organization: Separated posts:', orgPosts);

  // Use the feed hook to combine and sort feed items
  const { combinedFeed } = useFeed(
    orgGames,
    orgHighlights,
    [...orgPosts, ...(postsStore.posts || [])]
  );

  console.log('🔍 Organization: Combined feed:', combinedFeed);

  return (
    <div className="space-y-6">
      {/* Organization Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center">
          {targetOrganization.image_url && (
            <img
              src={targetOrganization.image_url}
              alt="Organization"
              className="w-16 h-16 rounded-lg object-contain mr-4"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{targetOrganization.name}</h1>
            {targetOrganization.team_name && (
              <p className="text-lg text-blue-600 font-medium">{targetOrganization.team_name}</p>
            )}
            <p className="text-gray-500 mt-1">
              {activeTab === 'feed' 
                ? `See recent games and highlights from players in ${targetOrganization.name}!`
                : `View all players in ${targetOrganization.name}`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('feed')}
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'feed'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center">
              <Activity size={20} className="mr-2" />
              Feed
            </div>
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
              activeTab === 'members'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center">
              <Users size={20} className="mr-2" />
              Members ({organizationStore.totalMembers})
            </div>
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'feed' ? (
            <FeedList
              items={combinedFeed}
              onEdit={(item) => {
                if (item.content_type === 'game') handleEditGame(item);
                else if (item.content_type === 'highlight') handleEditHighlight(item);
                else if (item.content_type === 'post') handleEditPost(item);
              }}
              onDelete={(item) => {
                if (item.content_type === 'game') handleDeleteGame(item.id);
                else if (item.content_type === 'highlight') handleDeleteHighlight(item.id);
                else if (item.content_type === 'post') handleDeletePost(item.id);
              }}
              onVideoPress={handleVideoPress}
              emptyMessage="No recent activity found"
              loading={organizationStore.loading}
              onLoadMore={handleLoadMore}
              hasMore={organizationStore.hasMoreFeed}
              className="space-y-4"
            />
          ) : (
            <div className="space-y-4">
              {organizationStore.members.length > 0 ? (
                organizationStore.members.map((member) => (
                  <div
                    key={member.id}
                    className="bg-white rounded-xl p-4 hover:bg-gray-50 transition-colors cursor-pointer border border-gray-200"
                    onClick={() => handleMemberPress(member)}
                  >
                    <div className="flex items-center">
                      {/* Member Avatar */}
                      {member.avatar ? (
                        <img
                          src={member.avatar}
                          alt={`${member.first_name} ${member.last_name}`}
                          className="w-12 h-12 rounded-full mr-4 object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center mr-4">
                          <span className="text-white font-bold text-lg">
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </span>
                        </div>
                      )}
                      
                      {/* Member Info */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-900 text-lg">
                              {member.first_name} {member.last_name}
                            </p>
                            <div className="flex items-center space-x-4 mt-1">
                              {member.level && (
                                <div className="flex items-center">
                                  <span className="text-xs text-gray-500 mr-1">Level</span>
                                  <span className="text-sm font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                    {member.level}
                                  </span>
                                </div>
                              )}
                              {member.age && (
                                <div className="flex items-center">
                                  <span className="text-xs text-gray-500 mr-1">Age</span>
                                  <span className="text-sm font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                    {member.age}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Role Badge */}
                          {member.role && (
                            <div className="text-right">
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                member.role === 'player' ? 'bg-blue-100 text-blue-700' :
                                member.role === 'coach' ? 'bg-purple-100 text-purple-700' :
                                member.role === 'parent' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Users className="mx-auto text-gray-400" size={48} />
                  <p className="mt-2 text-gray-500 font-medium">No members found</p>
                  <p className="text-sm text-gray-400">
                    Members of your organization will appear here.
                  </p>
                </div>
              )}
              {organizationStore.membersLoading && organizationStore.members.length > 0 && (
                <div className="text-center py-4">
                  <p className="text-gray-500">Loading more members...</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{selectedVideo.title}</h3>
              <button
                onClick={closeVideoModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-gray-500">Video player coming soon</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default Organization; 